import React from 'react';
import RuleRow from 'src/components/atomic/molecules/ruleRow/RuleRow';
import { Formik } from 'formik';
import { useRules, useUpdateRule } from 'src/hooks/queries/rules';
import Loading from 'src/components/atomic/atoms/loading/Loading';
import { CategoryModel } from 'src/types/category.model';
import { useCategories } from 'src/hooks/queries/categories';
import { getRuleInitialValues, getRuleValidationSchema, matchingRules } from 'src/utils/rule.util';
import { RuleModel } from 'src/types/rule.model';
import { RuleMatcher } from 'src/types/ruleMatcher.enum';
import LiteralsService from 'src/services/literals/LiteralsService';
import { useLocation } from 'react-router';
import styles from './createRuleBox.module.scss';
import Selectors from 'src/utils/selectors';
import IconButton from 'src/components/icon-button/IconButton';
import { ButtonType } from 'src/types/button-type.enum';
import { IconClose } from '@americanexpress/dls-icons';
import { DLSSize } from 'src/types/dls-size.enum';
import { Alert } from '@americanexpress/dls-react';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import ApplyButton from 'src/components/atomic/atoms/applyButton/ApplyButton';
import ruleStyles from 'src/styles/rule.module.scss';

interface CreateRuleBoxWrapperProps {
    handleClose: () => void;
}

export default function CreateRuleBoxWrapper({ handleClose }: CreateRuleBoxWrapperProps): JSX.Element {
    const { isLoading: isLoadingRules, data: rules } = useRules();
    const { isLoading: isLoadingCategories, data: categories } = useCategories();
    const { mutateAsync: updateRule } = useUpdateRule();
    const { pathname } = useLocation();
    const isLoading = isLoadingRules || isLoadingCategories;
    const rule: Partial<RuleModel> = {
        matcher: RuleMatcher.CONTAINS
    };
    const handleSubmit = async (newRule: Partial<RuleModel>): Promise<void> => {
        await updateRule(newRule);
        trackClick(pathname, adobeTags.Rules.Create);
        handleClose();
    };

    return (
        <CreateRuleBoxFormikWrapper
            rule={rule}
            rules={rules}
            categories={categories}
            pathname={pathname}
            handleSubmit={handleSubmit}
            handleClose={handleClose}
            isLoading={isLoading}
        />
    );
}

export interface CreateRuleBoxFormikWrapperProps {
    rule: Partial<RuleModel>;
    rules: RuleModel[];
    categories: CategoryModel[];
    pathname: string;
    handleSubmit: (newRule: Partial<RuleModel>) => Promise<void>;
    handleClose: () => void;
    isLoading?: boolean;
}

export function CreateRuleBoxFormikWrapper({
    rule,
    rules,
    categories,
    pathname,
    handleSubmit,
    handleClose,
    isLoading
}: CreateRuleBoxFormikWrapperProps): JSX.Element {
    const [showRuleAlreadyExistsError, setShowRuleAlreadyExistsError] = React.useState(false);

    return (
        <Formik
            initialValues={getRuleInitialValues(rule)}
            key={rule?.id}
            enableReinitialize
            validationSchema={getRuleValidationSchema()}
            onSubmit={async (values) => {
                const newRule: Partial<RuleModel> = {
                    matcher: values.ruleMatcher,
                    transactionName: values.ruleTransactionName,
                    groupUid: values.ruleGroup
                };

                let error = false;
                rules?.forEach((existingRule) => {
                    if (matchingRules(newRule, existingRule)) {
                        error = true;
                        setShowRuleAlreadyExistsError(true);
                        return null;
                    }
                });

                if (!error) {
                    await handleSubmit(newRule);
                }
            }}
        >
            <CreateRuleBox
                rule={rule}
                pathname={pathname}
                isLoading={isLoading}
                categories={categories}
                handleClose={handleClose}
                showRuleAlreadyExistsError={showRuleAlreadyExistsError}
            />
        </Formik>
    );
}

export interface CreateRuleBoxProps {
    rule: Partial<RuleModel>;
    pathname: string;
    isLoading?: boolean;
    categories: CategoryModel[];
    handleClose: () => void;
    showRuleAlreadyExistsError?: boolean;
}

export function CreateRuleBox({
    rule,
    pathname,
    categories,
    isLoading,
    handleClose,
    showRuleAlreadyExistsError
}: CreateRuleBoxProps): JSX.Element {
    if (isLoading) return <Loading />;

    return (
        <div className={styles.createRuleContainer} data-cy={Selectors.Transactions.CREATE_RULE_BOX} id="rule">
            <IconButton
                variant={ButtonType.PRIMARY_DARK}
                icon={<IconClose size={DLSSize.MEDIUM} />}
                title={LiteralsService.getLiteral('CreateRuleBox.close')}
                onClick={handleClose}
                className={styles.closeButton}
                data-cy={Selectors.SpendSmart.CLOSE_BUTTON}
            />
            <Typography
                variant={TypographyVariants.BODY_BOLD}
                component="h2"
                data-cy={Selectors.Transactions.CREATE_RULE_HEADER}
            >
                {LiteralsService.getLiteral('CreateRuleBox.title')}
            </Typography>
            <RuleRow
                rule={rule}
                pathname={pathname}
                formStyle={styles.ruleRow}
                button={
                    <ApplyButton
                        showText
                        secondary
                        aria-label={LiteralsService.getLiteral('Rule.applyRule')}
                        data-cy={Selectors.Transactions.APPLY_RULE_BUTTON}
                        className={ruleStyles.applyButton}
                    />
                }
                categories={categories}
            />
            {showRuleAlreadyExistsError && (
                <Alert id="ruleAlreadyExistsAlert" data-cy={Selectors.Transactions.RULE_ALREADY_EXISTS_ERROR}>
                    {LiteralsService.getLiteral('Rule.alreadyExists')}
                </Alert>
            )}
        </div>
    );
}
